import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useMySubscription } from "./useMySubscription";
import type {
  SubscriptionStatus,
  SubscriptionTier,
} from "../api/General/Subscription";

/**
 * The subscription-banner state machine, extracted from `SubscriptionBanner`.
 *
 * WHY IT LIVES HERE NOW: the band was not the only surface that has to answer
 * "is this tenant an upgrade audience?". `/brochure/:moduleSlug` renders the
 * per-module sales brochures and had NO gate at all — any role, any tier, any
 * status could deep-link to it, including a healthy long-term Paid tenant who
 * would be shown a pitch for a module they already pay for. Copying the
 * condition into the route would have given us two copies of a five-variant
 * state machine that drift; one shared derivation cannot.
 *
 * `state !== null` IS the condition the banner renders on, and it is therefore
 * the condition the brochure route admits on.
 *
 * ⚠️ ONE DELIBERATE DIFFERENCE, and it is not an oversight. The banner ALSO
 * stands down when `useBilling().isRestricted` — that is a *precedence* rule
 * between two bands competing for the same strip of screen (RestrictedBanner
 * carries the more severe true statement), not a statement about who the
 * upgrade audience is. A Restricted tenant is squarely an upgrade audience, and
 * `useBilling` is Hr/Admin-only anyway, so folding it in here would make the
 * brochures 404 for a Manager for reasons that have nothing to do with tier.
 * The route gates on tier/status only.
 */

export type BannerVariant =
  | "free"
  | "trial-healthy"
  | "trial-ending"
  | "trial-expired"
  | "paid-expiring";

export interface BannerState {
  variant: BannerVariant;
  tier: SubscriptionTier;
  status: SubscriptionStatus;
  daysRemaining: number | null;
}

export interface UseSubscriptionBannerStateResult {
  /** The banner state, or null when this tenant has nothing to be nudged about. */
  state: BannerState | null;
  /**
   * False while `/Subscription/me` is still in flight and we have no data at all.
   * A consumer that GATES on `state` must wait for this — `coerceTier(undefined)`
   * is "Free", so deciding early is deciding wrong in both directions.
   */
  isResolved: boolean;
}

export function useSubscriptionBannerState(): UseSubscriptionBannerStateResult {
  const location = useLocation();
  const { data, isLoading, isError, tier, status, isOperator, isFree, isTrial, isPaid } =
    useMySubscription();

  // -------------------- Operator preview --------------------
  // Operators can dogfood the banner without mutating company state via
  // ?previewTier=free|trial|paid&previewDays=N. The override is per-page-visit
  // and only honoured when the live response says we're an Operator — so
  // a regular user can't smuggle in a different banner state via URL.
  const previewState = useMemo<BannerState | null>(() => {
    const params = new URLSearchParams(location.search);
    const previewTier = params.get("previewTier");
    const previewDaysRaw = params.get("previewDays");
    if (!previewTier) return null;
    /*
      ⚠️ GATE ON A CONFIRMED OPERATOR, NOT ON "NOT YET DISPROVEN".
      The old test passed while `data` was still null, so during the /Subscription/me round-trip ANY
      user who landed on `?previewTier=trial-expired` — a pasted link, a shared URL — got the red
      "trial ended" band for as long as the request took. The override is a dogfooding tool, so it
      waits for the live response to say we are an Operator rather than assuming it until told
      otherwise.
    */
    if (!data || !(isOperator || data.isOperator)) return null;

    const previewDays = previewDaysRaw != null ? Number(previewDaysRaw) : null;
    const lower = previewTier.toLowerCase();
    if (lower === "free") {
      return {
        variant: "free",
        tier: "Free",
        status: "Active",
        daysRemaining: null,
      };
    }
    if (lower === "trial") {
      const days = previewDays ?? 14;
      return {
        variant: days <= 7 ? "trial-ending" : "trial-healthy",
        tier: "Trial",
        status: "Active",
        daysRemaining: days,
      };
    }
    if (lower === "trial-expired") {
      return {
        variant: "trial-expired",
        tier: "Free",
        status: "TrialExpired",
        daysRemaining: null,
      };
    }
    if (lower === "paid") {
      const days = previewDays ?? 21;
      return {
        variant: "paid-expiring",
        tier: "Paid",
        status: "Active",
        daysRemaining: days,
      };
    }
    return null;
  }, [location.search, isOperator, data]);

  // -------------------- Live state -> variant --------------------
  // Note: the spec keeps the banner hidden on healthy long-term Paid plans
  // (paidDaysRemaining is null OR > 30). We only nudge when renewal is near.
  const liveState = useMemo<BannerState | null>(() => {
    if (!data || isLoading || isError) return null;
    if (data.isOperator) return null;

    if (isFree) {
      if (status === "TrialExpired") {
        return {
          variant: "trial-expired",
          tier,
          status,
          daysRemaining: null,
        };
      }
      return {
        variant: "free",
        tier,
        status,
        daysRemaining: null,
      };
    }
    if (isTrial) {
      const days = data.trialDaysRemaining ?? 0;
      return {
        variant: days <= 7 ? "trial-ending" : "trial-healthy",
        tier,
        status,
        daysRemaining: days,
      };
    }
    if (isPaid) {
      /*
        A subscription the Operator has marked as never-expiring has no renewal to chase. Nudging
        it would contradict the billing page, which tells the same customer in plain words that
        their workspace has no end date. Silence beats a countdown to nothing.
      */
      if (data.neverExpires === true) return null;
      const days = data.paidDaysRemaining;
      if (days == null) return null; // indefinite Paid — silence the banner
      if (days >= 30) return null;
      return {
        variant: "paid-expiring",
        tier,
        status,
        daysRemaining: days,
      };
    }
    return null;
  }, [data, isLoading, isError, isFree, isTrial, isPaid, status, tier]);

  return {
    state: previewState ?? liveState,
    /*
      Resolved means the shared store has ANSWERED — it is deliberately NOT
      `!isLoading`. The store's initial snapshot is `{data:null, isLoading:false}`
      and stays that way until the mount effect fires `loadSubscription`, so a
      gate reading `!isLoading` would decide "no data ⇒ deny" on the very first
      render, before a request had even been made. An outright failure IS
      resolved: it will not become data by waiting, and `state` is null on that
      path, so the gate closes rather than spinning on a 500.
    */
    isResolved: data != null || isError,
  };
}
